import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery, gql } from '@apollo/client';

import { parameterize } from 'util/strings';
import GoogleMap from 'components/GoogleMap';
import GeoJSON from 'components/GoogleMap/GeoJSON';
import Spinner from 'components/Spinner';

import SubmarketNeighborhoodSelector from './SubmarketNeighborhoodSelector';
import styles from './style.module.scss';
import MarketSelector from './MarketSelector';

export const MARKET_QUERY = gql`
  query market($name: String!) {
    market(name: $name) {
      id
      name
      position {
        lat
        lng
      }
      __typename
      externalId
      seoString
      submarkets(visibility: VISIBLE) {
        id
        name
        geojson
        seoString
        position {
          lat
          lng
        }
        __typename
        externalId
        neighborhoods(visibility: [VISIBLE, HIDDEN]) {
          id
          name
          geojson
          parameterizedName
          yearlyPricePerFoot
          externalId
          visibility
          seoString
          submarkets {
            id
            externalId
          }
          __typename
        }
      }
      cities {
        id
        name
        position
        externalId
        neighborhoods {
          id
          name
          geojson
          parameterizedName
          yearlyPricePerFoot
          externalId
        }
      }
    }
  }
`;

const SQUAREFOOT_HQ = { lat: 40.741047, lng: -73.992917 };

const MarketLocationSelector = ({
  mapClassName,
  hideMobileMap,
  onMarketChange,
  selectedMarket,
  onSubmarketChange,
  additionalMapOptions,
  selectedNeighborhoods,
  onNeighborhoodChange,
  showMarketSelector,
  selectorsClassName,
  selectedSubmarkets,
  accessor,
}) => {
  const { data, loading, error } = useQuery(MARKET_QUERY, {
    variables: { name: selectedMarket.name },
    fetchPolicy: 'cache-first',
    skip: !selectedMarket,
  });

  if (error) throw error;

  if (loading) return <Spinner />;

  const allNeighborhoods =
    data.market[accessor].reduce(
      (acc, area) => [...acc, ...area.neighborhoods],
      []
    ) || [];
  const parsedSelectedNeighborhoods = allNeighborhoods.filter((neighborhood) =>
    selectedNeighborhoods
      .map(({ name }) => parameterize(name))
      .includes(neighborhood.parameterizedName)
  );

  const parsedSelectedSubmarkets = data.market.submarkets.filter((smarket) =>
    selectedSubmarkets
      .map(({ externalId }) => externalId)
      .includes(smarket.externalId)
  );

  const center = data?.market?.position || SQUAREFOOT_HQ;

  const mapClassNames = classNames(styles.map, mapClassName, {
    [styles.hideMap]: hideMobileMap,
  });

  return (
    <div className={styles.container}>
      <div className={classNames(styles.selectors, selectorsClassName)}>
        {showMarketSelector && (
          <MarketSelector
            name="market-selector"
            selectedMarket={data.market}
            onMarketChange={onMarketChange}
            className={styles.marketSelector}
          />
        )}

        {data.market[accessor].length > 0 && (
          <SubmarketNeighborhoodSelector
            subMarkets={data.market[accessor]}
            onSubmarketChange={onSubmarketChange}
            onNeighborhoodChange={onNeighborhoodChange}
            selectedNeighborhoods={parsedSelectedNeighborhoods}
            selectedSubmarkets={parsedSelectedSubmarkets}
            allNeighborhoods={allNeighborhoods}
          />
        )}
      </div>
      <GoogleMap
        autoCenter
        className={mapClassNames}
        options={{
          center,
          zoom: 12,
          scrollwheel: false,
          ...additionalMapOptions,
        }}
      >
        {parsedSelectedNeighborhoods.map((b) => (
          <GeoJSON key={b.name} data={b.geojson} />
        ))}
        {parsedSelectedSubmarkets.map((b) => (
          <GeoJSON key={b.name} data={b.geojson} />
        ))}
      </GoogleMap>
    </div>
  );
};

MarketLocationSelector.propTypes = {
  subMarket: PropTypes.shape({
    position: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  }),
  hideMobileMap: PropTypes.bool,
  mapClassName: PropTypes.string,
  additionalMapOptions: PropTypes.shape({}),
  onMarketChange: PropTypes.func.isRequired,
  selectedMarket: PropTypes.shape({
    name: PropTypes.string,
    position: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
  }).isRequired,
  onSubmarketChange: PropTypes.func.isRequired,
  selectedNeighborhoods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onNeighborhoodChange: PropTypes.func.isRequired,
  showMarketSelector: PropTypes.bool,
  selectorsClassName: PropTypes.string,
  selectedSubmarkets: PropTypes.arrayOf(PropTypes.shape({})),
  accessor: PropTypes.string,
};

MarketLocationSelector.defaultProps = {
  subMarket: null,
  mapClassName: null,
  hideMobileMap: true,
  additionalMapOptions: null,
  showMarketSelector: true,
  selectorsClassName: null,
  selectedSubmarkets: [],
  accessor: 'submarkets',
};

export default MarketLocationSelector;
